import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Presentation, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";

/**
 * Componente Slides - HTML Criativo Gerado por IA
 * Renderiza slides com designs únicos e criativos
 */

// Componente helper para renderizar HTML com style tags
const HtmlSlideContent = ({ htmlContent }) => {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (containerRef.current && htmlContent) {
      // Limpar container
      containerRef.current.innerHTML = '';
      
      // Criar um container temporário
      const temp = document.createElement('div');
      temp.innerHTML = htmlContent;
      
      // Transferir todos os nós (incluindo style tags)
      while (temp.firstChild) {
        containerRef.current.appendChild(temp.firstChild);
      }
    }
  }, [htmlContent]);

  return (
    <div 
      ref={containerRef}
      className="rounded-xl overflow-hidden shadow-2xl"
      style={{ minHeight: '450px' }}
    />
  );
};

const Slides = ({ slidesData }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Reset ao mudar de dados (novo vídeo)
  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [slidesData]);

  // Debug: log dos slides recebidos
  useEffect(() => {
    if (slidesData && slidesData.length > 0) {
      console.log('📊 Slides recebidos:', {
        total: slidesData.length,
        primeiro: slidesData[0],
        temHtmlContent: !!slidesData[0].htmlContent,
        htmlLength: slidesData[0].htmlContent ? slidesData[0].htmlContent.length : 0
      });
    }
  }, [slidesData]);

  if (!slidesData || slidesData.length === 0) {
    return (
      <Card className="card">
        <CardContent className="p-8 text-center">
          <Presentation className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Slides não disponíveis para este vídeo</p>
        </CardContent>
      </Card>
    );
  }

  const currentSlide = slidesData[currentSlideIndex];
  const totalSlides = slidesData.length;

  const handleNext = () => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const handleReset = () => {
    setCurrentSlideIndex(0);
  };

  const goToSlide = (index) => {
    setCurrentSlideIndex(index);
  };

  return (
    <Card className="card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Presentation className="w-5 h-5 text-indigo-600" />
            Slides de Resumo
          </CardTitle>
          <Badge variant="secondary">
            Slide {currentSlideIndex + 1} de {totalSlides}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg p-4">
          <p className="text-sm text-indigo-800">
            🎨 Designs únicos e criativos gerados por IA. Cada slide é diferente!
          </p>
        </div>

        {/* Slide HTML Criativo */}
        {currentSlide.htmlContent ? (
          <HtmlSlideContent htmlContent={currentSlide.htmlContent} />
        ) : (
          <div className="rounded-xl overflow-hidden shadow-2xl p-8 bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center" style={{ minHeight: '450px' }}>
            <div className="text-center">
              <p className="text-lg">Conteúdo do slide não disponível</p>
              <p className="text-sm opacity-75 mt-2">ID: {currentSlide.id}</p>
              <p className="text-sm opacity-75 mt-1">Título: {currentSlide.title}</p>
            </div>
          </div>
        )}

        {/* Controles de navegação */}
        <div className="flex items-center justify-between gap-3">
          <Button
            onClick={handlePrevious}
            disabled={currentSlideIndex === 0}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>

          <div className="flex-1 flex gap-2 justify-center overflow-x-auto px-2">
            {slidesData.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  index === currentSlideIndex
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <Button
            onClick={handleNext}
            disabled={currentSlideIndex === totalSlides - 1}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            Próximo
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Reset button */}
        <div className="flex justify-center">
          <Button
            onClick={handleReset}
            variant="ghost"
            size="sm"
            className="gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Voltar ao Início
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Slides;
